import { DocxReviewer } from "@eigenpal/docx-editor-agents";
import JSZip from "jszip";

/**
 * Negotiation handoff for .docx files: the clean copy opposing counsel gets,
 * the with-markup copy the attorney keeps, and plain-text projections used to
 * compare versions. Everything here works on detached buffers; the live
 * editor's redline is never touched.
 */

const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * Every WordprocessingML element that carries review state: tracked
 * insertions/deletions, moves, property-change records, table revision marks
 * and comments. Prefix-agnostic so an unusual namespace prefix cannot slip
 * markup past the scan.
 */
export const REVIEW_MARKUP =
  /<(?:[\w.-]+:)?(?:ins|del|delText|delInstrText|conflictIns|conflictDel|move(?:From|To)(?:Range(?:Start|End))?|customXml(?:Ins|Del|MoveFrom|MoveTo)Range(?:Start|End)|\w+PrChange|tblGridChange|numberingChange|cellIns|cellDel|cellMerge|comment|commentRangeStart|commentRangeEnd|commentReference)\b/;

export const hasReviewMarkup = (xml: string): boolean =>
  REVIEW_MARKUP.test(xml);

/** The clean copy could not be produced without leaving review markup behind. */
export class MarkupRemainsError extends Error {
  constructor(public readonly parts: string[]) {
    super(
      "This document contains review markup that cannot yet be removed safely. Download a copy with markup and finish the clean copy in Word."
    );
    this.name = "MarkupRemainsError";
  }
}

const COMMENT_PARTS = /^word\/comments[^/]*\.xml$/;
const COMMENT_ROOT =
  /(<(?:[\w.-]+:)?(?:comments|commentsEx|commentsIds|commentsExtensible)\b[^>]*>)[\s\S]*(<\/(?:[\w.-]+:)?(?:comments|commentsEx|commentsIds|commentsExtensible)>)/;

/**
 * Build the clean copy on a detached reviewer: accept every tracked change
 * (footnotes and endnotes included), remove every comment, then empty the
 * comment parts the serializer keeps around. Finally scan every `word/*.xml`
 * part and refuse rather than ship a file that is only partly clean.
 */
export const createCleanDocx = async (
  buffer: ArrayBuffer
): Promise<ArrayBuffer> => {
  const reviewer = await DocxReviewer.fromBuffer(buffer.slice(0));
  reviewer.acceptAll({ includeFootnotes: true, includeEndnotes: true });
  for (const comment of reviewer.getComments()) {
    reviewer.removeComment(comment.id);
  }
  const output = await reviewer.toBuffer();
  const zip = await JSZip.loadAsync(output);
  const entries = Object.entries(zip.files);
  await Promise.all(
    entries
      .filter(([path]) => COMMENT_PARTS.test(path))
      .map(async ([path, file]) => {
        const xml = await file.async("string");
        zip.file(path, xml.replace(COMMENT_ROOT, "$1$2"));
      })
  );
  // Re-resolve each part: the entries above point at the pre-clearing objects.
  const scanned = await Promise.all(
    entries
      .filter(([path]) => path.startsWith("word/") && path.endsWith(".xml"))
      .map(async ([path]) => ({
        path,
        dirty: hasReviewMarkup((await zip.file(path)?.async("string")) ?? "")
      }))
  );
  const dirty = scanned.filter((part) => part.dirty).map((part) => part.path);
  if (dirty.length > 0) throw new MarkupRemainsError(dirty);
  return zip.generateAsync({ type: "arraybuffer" });
};

/** `Brief.docx` → `Brief (clean).docx`; a missing extension is added. */
export const cleanCopyFilename = (filename: string): string => {
  const base = filename.replace(/\.docx$/i, "");
  return `${base} (clean).docx`;
};

export const downloadDocx = (bytes: ArrayBuffer, filename: string): void => {
  const url = URL.createObjectURL(new Blob([bytes], { type: DOCX_MIME }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const decodeEntities = (text: string): string =>
  text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, code: string) =>
      String.fromCodePoint(Number(code))
    )
    .replace(/&#x([0-9a-f]+);/gi, (_, code: string) =>
      String.fromCodePoint(parseInt(code, 16))
    )
    .replace(/&amp;/g, "&");

/**
 * The text an attorney would read: one line per paragraph, tracked
 * deletions dropped, insertions kept, tabs and line breaks preserved. Pure
 * string work on `word/document.xml` so it runs anywhere and needs no editor.
 */
export const documentXmlToText = (xml: string): string => {
  const body = xml
    // Deleted runs are not part of the text as it reads.
    .replace(/<(?:[\w.-]+:)?del\b[^>]*>[\s\S]*?<\/(?:[\w.-]+:)?del>/g, "")
    .replace(
      /<(?:[\w.-]+:)?delText\b[^>]*>[\s\S]*?<\/(?:[\w.-]+:)?delText>/g,
      ""
    );
  // Only text runs and structural breaks count; whitespace between tags is
  // formatting of the XML, not of the document.
  const token =
    /<(?:[\w.-]+:)?t\b[^>]*>([\s\S]*?)<\/(?:[\w.-]+:)?t>|<(?:[\w.-]+:)?tab\b[^>]*\/>|<(?:[\w.-]+:)?(?:br|cr)\b[^>]*\/>|<\/(?:[\w.-]+:)?p>/g;
  let text = "";
  for (const match of body.matchAll(token)) {
    if (match[1] !== undefined) text += decodeEntities(match[1]);
    else if (/tab\b/.test(match[0])) text += "\t";
    else text += "\n";
  }
  return text
    .split("\n")
    .map((line) => line.replace(/\s+$/, ""))
    .join("\n")
    .replace(/^\n+|\n+$/g, "");
};

export const docxToText = async (buffer: ArrayBuffer): Promise<string> => {
  const zip = await JSZip.loadAsync(buffer.slice(0));
  const part = zip.file("word/document.xml");
  if (!part) return "";
  return documentXmlToText(await part.async("string"));
};

export type DiffLine = {
  kind: "same" | "added" | "removed";
  text: string;
};

/**
 * Line-level diff (longest common subsequence). Big enough for a brief, small
 * enough to run on click; the cap keeps a pathological document from locking
 * the tab, degrading to a whole-document replace instead.
 */
export const diffLines = (
  before: string,
  after: string,
  maxCells = 4_000_000
): DiffLine[] => {
  const a = before ? before.split("\n") : [];
  const b = after ? after.split("\n") : [];
  if (a.length * b.length > maxCells) {
    return [
      ...a.map((text) => ({ kind: "removed" as const, text })),
      ...b.map((text) => ({ kind: "added" as const, text }))
    ];
  }
  const width = b.length + 1;
  const table = new Uint32Array((a.length + 1) * width);
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i * width + j] =
        a[i] === b[j]
          ? table[(i + 1) * width + j + 1] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i] });
      i += 1;
      j += 1;
    } else if (table[(i + 1) * width + j] >= table[i * width + j + 1]) {
      out.push({ kind: "removed", text: a[i] });
      i += 1;
    } else {
      out.push({ kind: "added", text: b[j] });
      j += 1;
    }
  }
  while (i < a.length) {
    out.push({ kind: "removed", text: a[i] });
    i += 1;
  }
  while (j < b.length) {
    out.push({ kind: "added", text: b[j] });
    j += 1;
  }
  return out;
};

export const diffSummary = (
  lines: DiffLine[]
): { added: number; removed: number } =>
  lines.reduce(
    (acc, line) => {
      if (line.kind === "added") acc.added += 1;
      if (line.kind === "removed") acc.removed += 1;
      return acc;
    },
    { added: 0, removed: 0 }
  );
